import ReactFlow, {
  applyNodeChanges,
  ControlButton,
  Controls,
  EdgeChange,
  EdgeProps,
  MarkerType,
  Node,
  NodeChange,
  NodeProps,
} from "reactflow";
import { useAppDispatch, useAppSelector } from "store/hooks";
import "reactflow/dist/style.css";
import {
  changeNodeData,
  clearActivationPreflightIssues,
  clearNodeRemovalHistory,
  ConnectionStatus,
  deselectNodes,
  handleDevModeState,
  recountAvailableNodes,
  removeNode,
  removeNodeWithUndo,
  resetDevMode,
  selectNode,
  setJumpToTargettingNode,
  setNodes,
  undoNodeRemoval,
} from "reducers/flow-builder.reducer";
import { toast } from "react-toastify";
import {
  EmptyNode,
  ExitNode,
  InsertNode,
  JumpToNode,
  MessageNode,
  StartNode,
  TimeDelayNode,
  TimeWindowNode,
  TrackerNode,
  UserAttributeNode,
  WaitUntilNode,
  RicochetNode,
} from "./Nodes";
import FlowBuilderSidePanel from "./SidePanel/FlowBuilderSidePanel";
import { BranchEdge, PrimaryEdge } from "./Edges";
import { FC, useCallback, useEffect, useMemo, useRef } from "react";
import NodeDraggingProvider from "./FlowPlugins/NodeDraggingProvider";
import CtrlDragPanProvider from "./FlowPlugins/CtrlDragPanProvider";
import Button, { ButtonType } from "components/Elements/Buttonv2";
import { JumpToNodeData } from "./Nodes/NodeData";
import { DevModeControlHint } from "./DevModeControlHint";
import useDevKeysHandler from "./useDevKeysHandler";
import { useDevSocket } from "./useDevSocketConnection";
import { MultisplitNode } from "./Nodes/MultisplitNode";
import { ExperimentNode } from "./Nodes/ExperimentNode";
import { AiResponseNode } from "./Nodes/AiResponseNode";
import { ResponseBranchNode } from "./Nodes/ResponseBranchNode";
import { NodePreflightFrame, PreflightIssuesBanner } from "./PreflightIssues";

export enum NodeType {
  START = "start",
  EMPTY = "empty",
  MESSAGE = "message",
  JUMP_TO = "jumpTo",
  EXIT = "exit",
  WAIT_UNTIL = "waitUntil",
  TIME_DELAY = "timeDelay",
  PUSH = "push",
  TIME_WINDOW = "timeWindow",
  USER_ATTRIBUTE = "userAttribute",
  INSERT_NODE = "insertNode",
  TRACKER = "tracker",
  MULTISPLIT = "multisplit",
  EXPERIMENT = "experiment",
  AI_RESPONSE = "aiResponse",
  RESPONSE_BRANCH = "responseBranch",
  RICOCHET = "ricochet",
}

export enum EdgeType {
  PRIMARY = "primary",
  BRANCH = "branch",
}

// Drag-to-delete: wrap each user-deletable node component with a draggable
// shell so the browser handles the drag natively (same mechanism the drawer
// uses). On dragend we check whether the cursor was released outside the
// React Flow canvas bounds; if so, dispatch removeNode.
//
// We use a custom dataTransfer key ("drag-to-delete-id") so the drawer's
// existing drop handler in NodeDraggingProvider — which reads "action" — is
// unaffected.
const SCAFFOLDING_NODE_TYPES: string[] = [
  NodeType.START,
  NodeType.EMPTY,
  NodeType.INSERT_NODE,
];

// How far the cursor has to travel from the dragstart point before we
// consider the gesture an intentional "drag off the workflow." Anything less
// is treated as an aborted drag and the node stays put.
const DRAG_TO_DELETE_THRESHOLD_PX = 120;

const withDragToDelete = (
  Component: FC<NodeProps<any>>
): FC<NodeProps<any>> => {
  const Wrapped: FC<NodeProps<any>> = (props) => {
    const dispatch = useAppDispatch();
    const isViewMode = useAppSelector((state) => state.flowBuilder.isViewMode);
    const stepperIndex = useAppSelector(
      (state) => state.flowBuilder.stepperIndex
    );
    const dragStart = useRef<{ x: number; y: number } | null>(null);
    const didDragRef = useRef(false);

    // Don't make nodes draggable in the read-only viewer (active/paused/
    // stopped workflows), the review step, or for scaffolding node types.
    if (
      isViewMode ||
      stepperIndex === 3 ||
      SCAFFOLDING_NODE_TYPES.includes(props.type as string)
    ) {
      return <Component {...props} />;
    }

    return (
      <div
        draggable
        onDragStart={(e) => {
          didDragRef.current = false;
          dragStart.current = { x: e.clientX, y: e.clientY };
          e.dataTransfer.setData("drag-to-delete-id", props.id);
          e.dataTransfer.effectAllowed = "move";
        }}
        onDragEnd={(e) => {
          const start = dragStart.current;
          dragStart.current = null;
          if (!start) return;
          const dx = e.clientX - start.x;
          const dy = e.clientY - start.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          didDragRef.current = distance > 3;
          const flowEl = document.querySelector(".react-flow");
          const flowBounds = flowEl?.getBoundingClientRect();
          const releasedOutsideFlow =
            !!flowBounds &&
            (e.clientX < flowBounds.left ||
              e.clientX > flowBounds.right ||
              e.clientY < flowBounds.top ||
              e.clientY > flowBounds.bottom);
          if (releasedOutsideFlow && distance > DRAG_TO_DELETE_THRESHOLD_PX)
            dispatch(removeNodeWithUndo(props.id));
        }}
        onClick={(e) => {
          if (didDragRef.current) {
            didDragRef.current = false;
            return;
          }
          e.stopPropagation();
          dispatch(selectNode(props.id));
        }}
      >
        <Component {...props} />
      </div>
    );
  };
  return Wrapped;
};

const withPreflightIssues = (
  Component: FC<NodeProps<any>>
): FC<NodeProps<any>> => {
  const Wrapped: FC<NodeProps<any>> = (props) => (
    <NodePreflightFrame nodeId={props.id} stepId={props.data?.stepId}>
      <Component {...props} />
    </NodePreflightFrame>
  );
  return Wrapped;
};

const nodeTypes: Record<NodeType, FC<NodeProps<any>>> = {
  [NodeType.START]: withPreflightIssues(StartNode),
  [NodeType.EMPTY]: withPreflightIssues(EmptyNode),
  [NodeType.MESSAGE]: withPreflightIssues(withDragToDelete(MessageNode)),
  [NodeType.JUMP_TO]: withPreflightIssues(withDragToDelete(JumpToNode)),
  [NodeType.EXIT]: withPreflightIssues(withDragToDelete(ExitNode)),
  [NodeType.WAIT_UNTIL]: withPreflightIssues(withDragToDelete(WaitUntilNode)),
  [NodeType.TIME_DELAY]: withPreflightIssues(withDragToDelete(TimeDelayNode)),
  [NodeType.TIME_WINDOW]: withPreflightIssues(withDragToDelete(TimeWindowNode)),
  [NodeType.USER_ATTRIBUTE]: withPreflightIssues(
    withDragToDelete(UserAttributeNode)
  ),
  [NodeType.INSERT_NODE]: withPreflightIssues(InsertNode),
  [NodeType.TRACKER]: withPreflightIssues(withDragToDelete(TrackerNode)),
  [NodeType.MULTISPLIT]: withPreflightIssues(withDragToDelete(MultisplitNode)),
  [NodeType.PUSH]: withPreflightIssues(withDragToDelete(MessageNode)),
  [NodeType.EXPERIMENT]: withPreflightIssues(withDragToDelete(ExperimentNode)),
  [NodeType.AI_RESPONSE]: withPreflightIssues(withDragToDelete(AiResponseNode)),
  [NodeType.RESPONSE_BRANCH]: withPreflightIssues(
    withDragToDelete(ResponseBranchNode)
  ),
  [NodeType.RICOCHET]: withPreflightIssues(withDragToDelete(RicochetNode)),
};

const edgeTypes: Record<EdgeType, FC<EdgeProps<any>>> = {
  [EdgeType.PRIMARY]: PrimaryEdge,
  [EdgeType.BRANCH]: BranchEdge,
};

interface FlowEditorProps {
  isViewMode?: boolean;
  className?: string;
  onMove?: () => void;
  onMoveEnd?: () => void;
}

export const nodeTypesNotConnectableByJumpTo: (string | undefined)[] = [
  NodeType.EMPTY,
  NodeType.INSERT_NODE,
  NodeType.JUMP_TO,
  NodeType.START,
];

const FlowEditor: FC<FlowEditorProps> = ({
  className,
  isViewMode,
  onMove = () => {},
  onMoveEnd = () => {},
}) => {
  const {
    nodes,
    edges,
    stepperIndex,
    isOnboarding,
    jumpToTargettingNode,
    devModeState,
    dragDeleteHistory,
    activationPreflight,
  } = useAppSelector((state) => state.flowBuilder);
  useDevKeysHandler();
  const socket = useDevSocket();
  const dispatch = useAppDispatch();
  const undoToastIdRef = useRef<string | number | null>(null);
  const undoCountRef = useRef(dragDeleteHistory.length);
  const userDismissedRef = useRef(false);

  // Drive the persistent Undo toast off the history stack so it re-appears
  // for the next-most-recent delete after each undo, until the stack drains.
  useEffect(() => {
    const count = dragDeleteHistory.length;
    const wasGrowing = count > undoCountRef.current;
    undoCountRef.current = count;

    if (count === 0) {
      if (undoToastIdRef.current !== null) {
        toast.dismiss(undoToastIdRef.current);
        undoToastIdRef.current = null;
      }
      userDismissedRef.current = false;
      return;
    }

    // A fresh delete cancels any prior manual dismissal — the user clearly
    // wants the affordance back.
    if (wasGrowing) {
      userDismissedRef.current = false;
    }

    if (undoToastIdRef.current !== null || userDismissedRef.current) {
      return;
    }

    const myToastId: string | number = toast(
      () => (
        <div className="flex items-center justify-between gap-3 pr-3 text-themed-text">
          <span>Step deleted</span>
          <button
            type="button"
            className="font-semibold text-[#6366F1] hover:underline"
            onClick={() => dispatch(undoNodeRemoval())}
          >
            Undo
          </button>
        </div>
      ),
      {
        autoClose: false,
        closeOnClick: false,
        className: "!bg-themed-card !text-themed-text",
        bodyClassName: "!text-themed-text",
        onClose: () => {
          if (undoToastIdRef.current === myToastId) {
            undoToastIdRef.current = null;
            // If the stack still has entries when the toast closes, the
            // user clicked the X (not Undo). Suppress the auto-reappear
            // so we don't fight them, but keep the history so the header
            // Undo button still works.
            if (undoCountRef.current > 0) {
              userDismissedRef.current = true;
            }
          }
        },
      }
    );
    undoToastIdRef.current = myToastId;
  }, [dragDeleteHistory.length, dispatch]);

  useEffect(() => {
    return () => {
      if (undoToastIdRef.current !== null) {
        toast.dismiss(undoToastIdRef.current);
        undoToastIdRef.current = null;
      }
      dispatch(clearNodeRemovalHistory());
    };
  }, [dispatch]);

  const onNodesChange = (changes: NodeChange[]) => {
    if (devModeState.status === ConnectionStatus.Connected) {
      changes = changes.filter((change) => change.type !== "select");
    } else {
      changes = changes.filter(
        (change) =>
          change.type === "select" &&
          nodes.find((node) => node.id === change.id)?.type !== NodeType.EMPTY
      );
    }

    // Skip empty dispatches — React Flow fires position changes on every
    // drag tick which we filter out, but dispatching anyway creates a new
    // nodes array each time, which causes the throttled save effect in
    // FlowBuilderv2 to PATCH the layout endpoint on every tick.
    if (changes.length === 0) return;

    dispatch(setNodes(applyNodeChanges(changes, nodes)));
  };

  const handleDevModeDBClick = (node: Node<any, string | undefined>) => {
    if (
      devModeState.status !== ConnectionStatus.Connected ||
      node.type === NodeType.START ||
      node.type === NodeType.EMPTY
    )
      return;

    dispatch(
      setNodes(
        applyNodeChanges(
          [
            {
              id: node.id,
              selected: true,
              type: "select",
            },
          ],
          nodes
        )
      )
    );
  };

  const handleDevModeClick = (node: Node<any, string | undefined>) => {
    if (devModeState.status !== ConnectionStatus.Connected) return;

    if (!devModeState.availableNodeToJump?.includes(node.id)) return;

    socket?.emit("moveToNode", node.id);
  };

  const handleNodeClick = (node: Node<any, string | undefined>) => {
    if (devModeState.status === ConnectionStatus.Connected) {
      handleDevModeClick(node);
      return;
    }

    // Allow selection in the read-only viewer too: clicking a node must open
    // its side panel. Previously view mode returned here, so once a node was
    // deselected (e.g. clicking the canvas) the panel could not be reopened.
    if (SCAFFOLDING_NODE_TYPES.includes(node.type as string)) {
      return;
    }

    dispatch(selectNode(node.id));
  };

  const onEdgesChange = (changes: EdgeChange[]) => {
    console.log(changes);

    // dispatch(setEdges(applyEdgeChanges(changes, edges)));
  };

  const flowRef = useRef<HTMLDivElement>(null);
  const reactFlowInstanceRef = useRef<any>(null);

  const renderableNodes = useMemo(
    () =>
      isViewMode ? nodes.filter((node) => node.type !== NodeType.EMPTY) : nodes,
    [nodes, isViewMode]
  );

  const selectedNode = nodes.find((node) => node.selected);

  const focusSelectedNode = useCallback(
    (duration = 300) => {
      if (!selectedNode || !reactFlowInstanceRef.current) return;
      reactFlowInstanceRef.current.fitView({
        nodes: [{ id: selectedNode.id }],
        padding: 0.45,
        duration,
      });
    },
    [selectedNode?.id]
  );

  useEffect(() => {
    focusSelectedNode();
  }, [focusSelectedNode]);

  useEffect(() => {
    if (!jumpToTargettingNode || !selectedNode) return;

    if (
      selectedNode.id === jumpToTargettingNode ||
      nodeTypesNotConnectableByJumpTo.includes(selectedNode.type)
    ) {
      dispatch(deselectNodes());
      return;
    }

    dispatch(
      changeNodeData({
        id: jumpToTargettingNode,
        data: {
          type: NodeType.JUMP_TO,
          targetId: selectedNode.id,
          stepId: nodes.find((node) => node.id === jumpToTargettingNode)?.data
            .stepId,
        } as JumpToNodeData,
      })
    );
    dispatch(setJumpToTargettingNode(undefined));
  }, [selectedNode, jumpToTargettingNode]);

  useEffect(() => {
    dispatch(recountAvailableNodes());
  }, [nodes]);

  useEffect(() => {
    dispatch(resetDevMode());

    return () => {
      dispatch(resetDevMode());
    };
  }, []);

  return (
    <div
      className={`relative w-full h-full bg-themed-background text-themed-text flex flex-col ${
        className ? className : ""
      }`}
    >
      {!isViewMode && activationPreflight.issues.length > 0 && (
        <PreflightIssuesBanner
          issues={activationPreflight.issues}
          issueCount={activationPreflight.issueCount}
          nodes={nodes}
          onSelectNode={(nodeId) => dispatch(selectNode(nodeId))}
          onDismiss={() => dispatch(clearActivationPreflightIssues())}
        />
      )}
      {jumpToTargettingNode && (
        <div className="w-full px-[16px] py-[10px] bg-[#6366F1] rounded-sm flex justify-center items-center gap-5 text-themed-background font-inter text-[16px] font-semibold leading-[24px]">
          <div>Navigate the workflow and select the target step</div>
          <Button
            type={ButtonType.SECONDARY}
            onClick={() => dispatch(setJumpToTargettingNode(undefined))}
          >
            Cancel
          </Button>
        </div>
      )}
      <div className="w-full h-full flex">
        <ReactFlow
          nodes={renderableNodes}
          edges={edges}
          onInit={(ev) => {
            reactFlowInstanceRef.current = ev;

            if (selectedNode) {
              ev.fitView({
                nodes: [{ id: selectedNode.id }],
                padding: 0.45,
              });
            } else {
              ev.fitView();
              ev.zoomTo(0.8);
            }

            if (!selectedNode && isViewMode && stepperIndex !== 2) {
              const { x, y } = ev.getViewport();
              ev.setViewport({ x: x - 200, y, zoom: 0.8 });
            }
          }}
          onNodeClick={(_, node) => handleNodeClick(node)}
          onNodeDoubleClick={(_, node) => handleDevModeDBClick(node)}
          onPaneClick={() => {
            if (devModeState.status === ConnectionStatus.Connected) return;
            dispatch(deselectNodes());
          }}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodesFocusable={false}
          onMove={onMove}
          onMoveEnd={onMoveEnd}
          zoomOnDoubleClick={devModeState.status !== ConnectionStatus.Connected}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          zoomOnScroll
          panOnScroll={false}
          panOnDrag={false}
          nodesDraggable={false}
          nodeOrigin={[0.5, 0.5]}
          defaultEdgeOptions={{
            markerEnd: {
              type: MarkerType.ArrowClosed,
              color: "#6b7280",
              width: 18,
              height: 18,
            },
            style: {
              stroke: "#6b7280",
              strokeWidth: 1.5,
            },
          }}
          ref={flowRef}
        >
          <NodeDraggingProvider flowRef={flowRef} />
          <CtrlDragPanProvider flowRef={flowRef} />
          <Controls
            showZoom={false}
            showInteractive={false}
            position="top-left"
            className="rounded-sm"
          >
            {devModeState.status !== ConnectionStatus.Disabled && (
              <DevModeControlHint />
            )}
          </Controls>
        </ReactFlow>
        {!isViewMode && !isOnboarding && stepperIndex === 1 && (
          <FlowBuilderSidePanel />
        )}
        {!isOnboarding && stepperIndex === 3 && <FlowBuilderSidePanel docked />}
      </div>
    </div>
  );
};

export default FlowEditor;
